import { SupportTicketMessage } from '@outlaw-oasis/types';
import { formatDateTime } from '@outlaw-oasis/utils';
import { Shield, Lock } from 'lucide-react';

interface TicketMessagesProps {
  messages: SupportTicketMessage[];
  showInternal?: boolean;
}

export function TicketMessages({ messages, showInternal = false }: TicketMessagesProps) {
  const filteredMessages = showInternal
    ? messages
    : messages.filter(m => !m.isInternal);

  if (filteredMessages.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted">
        No messages yet. Be the first to reply!
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredMessages.map((message) => (
        <div
          key={message.id}
          className={`p-4 rounded-lg border ${
            message.isInternal
              ? 'bg-purple-500/10 border-purple-500/30'
              : message.isAdminReply
              ? 'bg-gold/5 border-gold/30'
              : 'bg-leather border-gold/20'
          }`}
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              {message.user?.avatarUrl ? (
                <img
                  src={message.user.avatarUrl}
                  alt={message.user.username}
                  className="w-10 h-10 rounded-full border-2 border-gold/30"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                  <span className="text-gold font-medium">
                    {message.user?.username?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-cream">
                    {message.user?.username || 'Unknown'}
                  </span>
                  {message.isAdminReply && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gold/20 text-gold rounded">
                      <Shield className="w-3 h-3" />
                      Support
                    </span>
                  )}
                  {message.isInternal && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
                      <Lock className="w-3 h-3" />
                      Internal Note
                    </span>
                  )}
                </div>
                <div className="text-xs text-text-muted">
                  {formatDateTime(message.createdAt)}
                </div>
              </div>
            </div>
          </div>

          <div className="text-cream whitespace-pre-wrap leading-relaxed">
            {message.message}
          </div>

          {message.attachmentUrl && (
            <div className="mt-3 pt-3 border-t border-gold/10">
              <a
                href={message.attachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-bright"
              >
                Attachment: {message.attachmentName || 'Download'}
              </a>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
